import { Portal } from "@reach/portal";
import { QPSPushProgressIndicator } from "client/src/domain/EIF/QPSPushProgressIndicator/QPSPushProgressIndicator";
import { usePollingAPIJobs } from "client/src/hooks/apiJobs";
import { useWebSocketQPSPushStatusEvent } from "client/src/hooks/qps";
import clsx from "clsx";
import { createContext, use, useState } from "react";

import * as styles from "./QPSPushProgressOverlay.module.css";

import type { PropsWithChildren } from "react";

import type { APIJobTask } from "shared/types/APIJob";
import type { Client, ClientId } from "shared/types/Client";

export type ProgressOverlayStatus = "loading" | "success-with-warnings" | "success" | "error";

export type ProgressOverlayCase = {
  client: Client;
  status: ProgressOverlayStatus;
  /** One client can have one EIF push and one Onboarding Form push in the overlay. */
  task: APIJobTask;
};

type ContextValue = {
  cases: ProgressOverlayCase[];
  addClient: (client: Client, task?: APIJobTask) => void;
  updateStatus: (clientId: ClientId, status: ProgressOverlayStatus, task?: APIJobTask) => void;
  removeClient: (client: Client, task?: APIJobTask) => void;
  drawerIsOpen: boolean;
  setDrawerIsOpen: (isOpen: boolean) => void;
};

const isSameCase = (clientCase: ProgressOverlayCase, clientId: ClientId, task: APIJobTask) =>
  clientCase.client.id === clientId && clientCase.task === task;

/** @returns true when the overlay shows a running push of the task for the client. */
export const getIsPushInProgress = (
  cases: ProgressOverlayCase[],
  clientId: ClientId | undefined,
  task: APIJobTask,
) =>
  cases.some(
    (clientCase) => isSameCase(clientCase, clientId ?? "", task) && clientCase.status === "loading",
  );

export const ProgressOverlayContext = createContext<ContextValue | null>(null);

export const useProgressOverlayContext = () => {
  const context = use(ProgressOverlayContext);

  if (context == null) {
    throw new Error("useProgressOverlayContext must be used within a ProgressOverlayProvider");
  }

  return context;
};

export function ProgressOverlayProvider({ children }: PropsWithChildren) {
  const [cases, setCases] = useState<ContextValue["cases"]>([]);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);

  const addClient = (client: Client, task: APIJobTask = "EIF") => {
    document.body.setAttribute("data-bottom-right-side-overlay-open", "true");

    document.startViewTransition(() => {
      setCases((prevCases) => {
        let shouldPush = true;
        const nextCases: ProgressOverlayCase[] = [];
        for (const clientCase of prevCases) {
          if (isSameCase(clientCase, client.id, task)) {
            nextCases.push({ client, status: "loading", task });
            shouldPush = false;
          } else {
            nextCases.push(clientCase);
          }
        }

        if (shouldPush) {
          nextCases.push({ client, status: "loading", task });
        }

        return nextCases;
      });
    });
  };

  const updateStatus = (
    clientId: ClientId,
    status: ProgressOverlayStatus,
    task: APIJobTask = "EIF",
  ) => {
    setCases((prevCases) =>
      prevCases.map((clientCase) =>
        isSameCase(clientCase, clientId, task) ? { ...clientCase, status } : clientCase,
      ),
    );
  };

  const removeClient = (client: Client, task: APIJobTask = "EIF") => {
    setCases((prevCases) => {
      const nextCases = prevCases.filter((clientCase) => !isSameCase(clientCase, client.id, task));
      if (nextCases.length === 0) {
        document.body.removeAttribute("data-bottom-right-side-overlay-open");
      }
      return nextCases;
    });
  };

  const value: ContextValue = {
    cases,
    addClient,
    updateStatus,
    removeClient,
    drawerIsOpen,
    setDrawerIsOpen,
  };

  return <ProgressOverlayContext value={value}>{children}</ProgressOverlayContext>;
}

const API_JOB_POLLING_INTERVAL = 1000 * 60; // 1 minute

export function QPSPushProgressOverlay() {
  useWebSocketQPSPushStatusEvent();
  usePollingAPIJobs(API_JOB_POLLING_INTERVAL);

  const qpsProgressContext = useProgressOverlayContext();

  if (qpsProgressContext.cases.length === 0) {
    return null;
  }

  return (
    <Portal type="qps-push-progress-popover-portal">
      <div
        className={clsx(
          styles.qpsPushProgressOverlay,
          qpsProgressContext.drawerIsOpen && styles.drawerIsOpen,
        )}
      >
        <QPSPushProgressIndicator
          cases={qpsProgressContext.cases}
          onRemove={qpsProgressContext.removeClient}
        />
      </div>
    </Portal>
  );
}
